import type { RetryOptions } from '../../types';
import { getPayload } from './getPayload';
import { resolveRetryDelay } from './resolveRetryDelay';

const normalize = (number: number): number => Math.max(0, Math.round(number));

export type GetRetryDelayParams = {
  retryOptions: RetryOptions;
  numberOfFailedAttempts: number;
  response: Response;
};

const getRetryDelayUnnormalized = async ({
  retryOptions: { retryDelay, maxRetries },
  numberOfFailedAttempts,
  response,
}: GetRetryDelayParams): Promise<number> => {
  const body = await getPayload(response);

  // It is important that this is undefined rather than null when the
  // "Retry-After" header does not exist, so that it will give NaN when passed
  // to the Number constructor.
  const retryAfterHeader: string | undefined =
    response.headers.get('Retry-After') ?? undefined;

  // Use the Number constructor instead of parseInt to give NaN when the
  // "Retry-After" header is not set or is a date.
  const delayFromHeaderInSeconds = Number(retryAfterHeader);

  if (!isNaN(delayFromHeaderInSeconds)) {
    return delayFromHeaderInSeconds * 1000;
  }

  const delayFromBodyInSeconds = Number(
    (body as { message?: string })?.message?.match(/\d+/)?.[0],
  );

  if (!isNaN(delayFromBodyInSeconds)) {
    return delayFromBodyInSeconds * 1000;
  }

  // Set default delaySeconds if not set by response headers/body
  const delayFromRetryOptions = resolveRetryDelay({
    retryDelay,
    maxRetries,
    numberOfFailedAttempts,
    response,
  });

  return delayFromRetryOptions;
};

export const getRetryDelay = async (
  params: GetRetryDelayParams,
): Promise<number> => {
  const unnormalized = await getRetryDelayUnnormalized(params);

  return normalize(unnormalized);
};
